import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Switch,
  Tooltip,
  Popover,
} from '@mui/material';
import {
  SportsCricket,
  Search,
  Clear,
  Add,
  Delete,
  PlayArrow,
  ExpandMore,
  Check,
  PersonOutline,
  DragIndicator,
  ArrowUpward,
  ArrowDownward,
  Refresh,
  TrendingUp,
  FlightTakeoff,
} from '@mui/icons-material';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiService } from '../services/api';
import { getRoleColor, getRoleLabel } from '../utils/roles';
import { useAutopilotPage } from '../autopilot/registry';
import EditableBowlingDeployment from '../components/EditableBowlingDeployment';
import { XiBowlerPhaseProjection } from '../types/lineup';
import { 
  LEAGUE_CONFIG, 
  TEAM_LOGO_MAP, 
  DEFAULT_LEAGUE, 
  getTeamLogo, 
  getLeagueConfig 
} from '../config/leagues';
import { formatCrores, sumPrices } from '../utils/format';

// Player-pool cache invalidation: bump the version whenever the backend pool
// payload changes meaning (role rules, new fields); entries also expire by age.
const POOL_CACHE_VERSION = 4; // 4: prices now come from the salary season with shared corrections
const POOL_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

// Local storage hook for persistence
function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error loading ${key} from localStorage:`, error);
      return initialValue;
    }
  });

  const setValue = (value: T | ((prev: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Error saving ${key} to localStorage:`, error);
    }
  };

  return [storedValue, setValue];
}

interface TeamMapping {
  team_id: number;
  team_name: string;
  team: string;
  team_full_name?: string;
  team_color?: string;
}

interface LocalTeamPlayerDetail {
  player_id: number;
  player_name: string;
  nationality: string;
  role: string;
  bat_tvaa_per_over: number | null;
  bowl_tvaa_per_over: number | null;
  avg_bat_pos?: number;
  lineup_bat_pos?: string;
  max_tvaa?: number;
  balls_per_innings_bat?: number;
  balls_per_innings_bowl?: number;
  wicket_keeper?: number;
  price?: number; // contract price in INR crore; only populated for leagues with showSalary
}

interface SelectedPlayer extends LocalTeamPlayerDetail {
  batting_order: number;
  is_impact: number;
}

interface BowlingDeployment {
  'Bowler Name': string;
  'Powerplay Overs': number;
  'Middle Overs': number;
  'Slog/Death Overs': number;
  'Total Overs': number;
}

interface LineupEvaluation {
  lineup_num: number;
  lineup_players: string[];
  sub_details?: any;
  bat_tv: number;
  bowl_tv: number;
  '5_bowl_tv'?: number;
  '6_bowl_tv'?: number;
  '7_bowl_tv'?: number;
  '5_bowl_deployment'?: BowlingDeployment[];
  '6_bowl_deployment'?: BowlingDeployment[];
  '7_bowl_deployment'?: BowlingDeployment[];
  xi_bowler_phase_proj?: XiBowlerPhaseProjection[];
}

const isPhasePresent = (v: unknown): v is number =>
  v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v));

const buildDeploymentTvaaProj = (deployment: BowlingDeployment[] | undefined) =>
  (deployment ?? []).map((d: any) => {
    const ppRaw = d.tvaa_pp ?? d.pp;
    const moRaw = d.tvaa_mo ?? d.mo;
    const soRaw = d.tvaa_so ?? d.so;
    const hasPp = isPhasePresent(ppRaw);
    const hasMo = isPhasePresent(moRaw);
    const hasSo = isPhasePresent(soRaw);
    return {
      playerId: d.player_id || d.playerId || 0,
      bowlerName: d['Bowler Name'] || d.bowler_name || '',
      pp: hasPp ? Number(ppRaw) : null,
      mo: hasMo ? Number(moRaw) : null,
      so: hasSo ? Number(soRaw) : null,
      hasPp,
      hasMo,
      hasSo,
      hasAnyPhase: hasPp || hasMo || hasSo,
      hasAllPhases: hasPp && hasMo && hasSo,
    };
  });

// Helper functions for role labels and colors (used by helper components)


const LineupPlayground: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [teams, setTeams] = useState<TeamMapping[]>([]);
  // League selection state (persistent)
  const [selectedLeague, setSelectedLeague] = useLocalStorage<string>('lineup_playground_league', DEFAULT_LEAGUE);
  // Persistent state using localStorage
  const [selectedTeamId, setSelectedTeamId] = useLocalStorage<number | null>('lineup_playground_team_id', null);
  const [selectedTeamShort, setSelectedTeamShort] = useLocalStorage<string>('lineup_playground_team_short', '');
  const [selectedPlayers, setSelectedPlayers] = useLocalStorage<SelectedPlayer[]>('lineup_playground_selected_players', []);
  // Always use batting first (is_bat_first = 1)
  const isBattingFirst = 1;
  const [playerSource, setPlayerSource] = useLocalStorage<'team' | 'pool'>('lineup_playground_player_source', 'team');
  // Track previous team to detect team changes (initialize from localStorage)
  const [previousTeamId, setPreviousTeamId] = useState<number | null>(selectedTeamId);
  // Track if this is the first load after mount
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  // Non-persistent state
  const [allPlayers, setAllPlayers] = useState<LocalTeamPlayerDetail[]>([]);
  // Persistent player pool in localStorage (persists across module switches).
  // The cache carries a version + timestamp: bump POOL_CACHE_VERSION whenever the
  // backend's pool payload changes meaning (e.g. role rules), so stale browser
  // caches self-invalidate; entries also expire after POOL_CACHE_TTL_MS.
  const [playerPool, setPlayerPool] = useLocalStorage<LocalTeamPlayerDetail[]>('lineup_playground_player_pool', []);
  const [playerPoolMeta, setPlayerPoolMeta] = useLocalStorage<{ v: number; ts: number } | null>('lineup_playground_player_pool_meta', null);
  const playerPoolCached =
    !!playerPoolMeta &&
    playerPoolMeta.v === POOL_CACHE_VERSION &&
    Date.now() - playerPoolMeta.ts < POOL_CACHE_TTL_MS;
  const setPlayerPoolCached = (cached: boolean) =>
    setPlayerPoolMeta(cached ? { v: POOL_CACHE_VERSION, ts: Date.now() } : null);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [loadingPlayerPool, setLoadingPlayerPool] = useState(false);
  const [loadingEvaluation, setLoadingEvaluation] = useState(false);
  const [reloadingData, setReloadingData] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [nationalityFilter, setNationalityFilter] = useState('all');
  const [visiblePlayersLimit, setVisiblePlayersLimit] = useState(50); // Show 50 players initially for performance
  // Persisted so the evaluation survives a tab switch or reload; it is tied to the team it was run for.
  const [storedEvaluation, setStoredEvaluation] = useLocalStorage<{ team: number | null; result: LineupEvaluation } | null>('lineup_playground_evaluation', null);
  const evaluationResult: LineupEvaluation | null = storedEvaluation && storedEvaluation.team === selectedTeamId ? storedEvaluation.result : null;
  const setEvaluationResult = (r: LineupEvaluation | null) => setStoredEvaluation(r ? { team: selectedTeamId, result: r } : null);
  const [evaluationError, setEvaluationError] = useState('');
  // State for edited deployments
  const [editedDeployments, setEditedDeployments] = useLocalStorage<{
    '5'?: { deployment: any[]; tv: number };
    '6'?: { deployment: any[]; tv: number };
    '7'?: { deployment: any[]; tv: number };
  }>('lineup_playground_edited_deployments', {});

  // Calculate weighted Bowling TV based on edited deployments or original values
  const calculatedBowlingTV = useMemo(() => {
    if (!evaluationResult) {
      return null;
    }

    // Use edited deployment TV values if available, otherwise use original values from evaluationResult
    const bowl5 = editedDeployments['5']?.tv ?? evaluationResult['5_bowl_tv'] ?? 0;
    const bowl6 = editedDeployments['6']?.tv ?? evaluationResult['6_bowl_tv'] ?? 0;
    const bowl7 = editedDeployments['7']?.tv ?? evaluationResult['7_bowl_tv'] ?? 0;

    // Calculate weighted average: 0.19 * 5_bowl + 0.63 * 6_bowl + 0.18 * 7_bowl
    const weightedTV = 0.19 * bowl5 + 0.63 * bowl6 + 0.18 * bowl7;
    
    return weightedTV;
  }, [evaluationResult, editedDeployments]);
  // Auto-move toggle: when false, impact player won't auto-move to playing XI when a player is removed
  const [autoMoveImpactPlayer, setAutoMoveImpactPlayer] = useLocalStorage<boolean>('lineup_playground_auto_move', true);
  // Drag and drop state
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [draggedPlayer, setDraggedPlayer] = useState<LocalTeamPlayerDetail | null>(null);
  
  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Apply optimized order when returning from the batting order optimizer
  useEffect(() => {
    const optimized = (location.state as any)?.optimizedPlayers;
    if (optimized && Array.isArray(optimized) && optimized.length > 0) {
      setSelectedPlayers(
        optimized.map((p: any) => ({
          ...selectedPlayers.find((sp) => sp.player_id === p.player_id) || p,
          batting_order: p.batting_order,
          is_impact: p.is_impact ?? 0,
        })),
      );
      // Clear the state so it doesn't re-apply on re-renders
      window.history.replaceState({}, document.title);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    loadTeams();
  }, [selectedLeague]);
  
  // Handle league change - clear selections and reload data
  const handleLeagueChange = (newLeague: string) => {
    if (newLeague === selectedLeague) return;
    
    console.log('[DEBUG] League changed from', selectedLeague, 'to', newLeague);
    
    // Clear all selections and data
    setSelectedTeamId(null);
    setSelectedTeamShort('');
    setSelectedPlayers([]);
    setAllPlayers([]);
    setTeams([]);
    setEvaluationResult(null);
    setEvaluationError('');
    setError('');
    
    // Clear player pool cache when league changes
    setPlayerPoolCached(false);
    setPlayerPool([]);
    
    // Set the new league (this will trigger loadTeams via useEffect)
    setSelectedLeague(newLeague);
  };
  
  // Get current league config
  const currentLeagueConfig = getLeagueConfig(selectedLeague);
  // Contract prices are only displayed for leagues whose salary table uses one unit (IPL: INR crore).
  const toPrice = (value: unknown): number | undefined =>
    currentLeagueConfig.showSalary && typeof value === 'number' && !Number.isNaN(value) ? value : undefined;

  // Debounce search query to improve performance (increased delay for better UX)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      // Reset visible limit when search changes
      setVisiblePlayersLimit(50);
    }, 500); // 500ms delay for better performance

    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Reset visible limit when role or nationality filter changes
  useEffect(() => {
    setVisiblePlayersLimit(50);
  }, [roleFilter, nationalityFilter]);
  
  // Get unique nationalities from all players for filter dropdown
  const uniqueNationalities = useMemo(() => {
    const nationalities = allPlayers
      .map(p => p.nationality)
      .filter((nat): nat is string => nat !== null && nat !== undefined && nat !== 'Unknown' && nat.trim() !== '')
      .filter((nat, index, self) => self.indexOf(nat) === index) // Get unique values
      .sort();
    return nationalities;
  }, [allPlayers]);

  useEffect(() => {
    // Load team players when team is selected (including from localStorage)
    if (playerSource === 'team' && selectedTeamId && selectedTeamShort) {
      loadTeamPlayers(selectedTeamShort);
    } else if (playerSource === 'pool') {
      // Use cached player pool from localStorage if available
      if (playerPoolCached && playerPool.length > 0) {
        console.log('[DEBUG] Using cached player pool from localStorage');
        setAllPlayers(playerPool);
        setError('');
    } else {
        // Only load from API if not cached
        console.log('[DEBUG] Player pool not cached, loading from API...');
        loadPlayerPool();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamId, selectedTeamShort, playerSource, selectedLeague]);
  
  // Note: previousTeamId is already initialized from selectedTeamId (which comes from localStorage)
  // No need for additional initialization

  const loadTeams = async () => {
    try {
      setLoadingTeams(true);
      // Use league code (e.g., 'THE HUNDRED') instead of key (e.g., 'THE_HUNDRED') for API calls
      const leagueCode = getLeagueConfig(selectedLeague).code;
      const response = await apiService.getTeamMappingsV2(leagueCode);
      console.log('[DEBUG] Team mappings response for league', leagueCode, ':', response);
      
      if (response && response.team_mappings) {
        console.log('[DEBUG] Team mappings array:', response.team_mappings);
        console.log('[DEBUG] First team sample:', response.team_mappings[0]);
        
        // Sort teams to put Rajasthan Royals (RR) at the start
        const teamsArray = [...response.team_mappings];
        const rrIndex = teamsArray.findIndex(t => {
          const teamCode = (t.team || t.team_name || '').toUpperCase();
          return teamCode === 'RR' || teamCode === 'RAJASTHAN ROYALS';
        });
        
        // If RR exists, move it to the start
        if (rrIndex !== -1 && rrIndex !== 0) {
          const rrTeam = teamsArray.splice(rrIndex, 1)[0];
          teamsArray.unshift(rrTeam);
        }
        
        console.log('[DEBUG] Teams array after sorting:', teamsArray);
        setTeams(teamsArray);
      } else {
        console.error('[DEBUG] No team_mappings in response:', response);
        setError('No teams found in response');
      }
    } catch (err: any) {
      console.error('[DEBUG] Error loading teams:', err);
      setError(`Failed to load teams: ${err.message || err.detail || 'Unknown error'}`);
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleReloadData = async () => {
    try {
      setReloadingData(true);
      setError('');
      // Use league code for API calls
      const leagueCode = getLeagueConfig(selectedLeague).code;
      console.log('[DEBUG] Reloading data from BigQuery for league:', leagueCode);
      
      const response = await apiService.reloadDataV2(leagueCode);
      console.log('[DEBUG] Reload response:', response);
      
      if (response.success) {
        // Clear cached player pool to force reload
        setPlayerPoolCached(false);
        setPlayerPool([]);
        
        // Reload teams
        await loadTeams();
        
        // Reload players based on current source
        if (playerSource === 'team' && selectedTeamId && selectedTeamShort) {
          await loadTeamPlayers(selectedTeamShort);
        } else if (playerSource === 'pool') {
          await loadPlayerPool();
        }
        
        // Show success message briefly
        setError(''); // Clear any previous error
        alert(`Data reloaded successfully! ${response.players_count || 0} players loaded.`);
      } else {
        throw new Error(response.message || 'Failed to reload data');
      }
    } catch (err: any) {
      console.error('[DEBUG] Error reloading data:', err);
      setError(`Failed to reload data: ${err.message || err.detail || 'Unknown error'}`);
    } finally {
      setReloadingData(false);
    }
  };

  const loadPlayerPool = async () => {
    // Skip if already cached in localStorage
    if (playerPoolCached && playerPool.length > 0) {
      console.log('[DEBUG] Using cached player pool from localStorage (not reloading)');
      setAllPlayers(playerPool);
      setError('');
      return;
    }

    try {
      setLoadingPlayerPool(true);
      setError('');
      // Use league code for API calls
      const leagueCode = getLeagueConfig(selectedLeague).code;
      console.log('[DEBUG] Loading player pool from API for league:', leagueCode);
      
      const data = await apiService.getPlayerPool(leagueCode);
      console.log('[DEBUG] Player pool data received:', data);
      console.log('[DEBUG] Players count:', data?.players?.length);
      
      if (!data || !data.players) {
        console.error('[DEBUG] No player pool data received from API');
        throw new Error('No player pool data received from API');
      }
      
      // Convert PlayerPoolPlayerDetail to LocalTeamPlayerDetail format
      // Keep all players - those without avg_bat_pos will still be shown (they'll have 'bowler' role)
      const playersWithIds: LocalTeamPlayerDetail[] = data.players.map((p: any) => ({
        player_id: p.player_id || 0,
        player_name: p.player_name,
        nationality: p.nationality || 'Unknown',
        role: p.role || 'bowler', // Ensure role is always set (default to 'bowler' if missing)
        bat_tvaa_per_over: p.bat_tvaa_per_over,
        bowl_tvaa_per_over: p.bowl_tvaa_per_over,
        avg_bat_pos: p.avg_bat_pos !== null && p.avg_bat_pos !== undefined && !isNaN(p.avg_bat_pos) ? p.avg_bat_pos : undefined,
        max_tvaa: p.max_tvaa !== null && p.max_tvaa !== undefined ? p.max_tvaa : undefined, // Keep null as undefined (not 0)
        balls_per_innings_bat: p.balls_per_innings_bat !== null && p.balls_per_innings_bat !== undefined ? p.balls_per_innings_bat : undefined,
        balls_per_innings_bowl: p.balls_per_innings_bowl !== null && p.balls_per_innings_bowl !== undefined ? p.balls_per_innings_bowl : undefined,
        wicket_keeper: p.wicket_keeper !== undefined && p.wicket_keeper !== null ? p.wicket_keeper : undefined,
        price: toPrice(p.price),
      }));
      
      // Debug: Log role distribution
      const roleCounts = playersWithIds.reduce((acc, p) => {
        acc[p.role] = (acc[p.role] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      console.log('[DEBUG] Player pool role distribution:', roleCounts);
      
      console.log('[DEBUG] Converted players:', playersWithIds.length);
      
      // Cache the player pool in localStorage (persists across module switches)
      setPlayerPool(playersWithIds);
      setPlayerPoolCached(true);
      setAllPlayers(playersWithIds); // Set allPlayers for compatibility with existing code
      
      console.log('[DEBUG] Player pool cached successfully');
    } catch (err: any) {
      console.error('[DEBUG] Error loading player pool:', err);
      setError(`Failed to load player pool: ${err.message || err.detail || 'Unknown error'}`);
      setPlayerPool([]);
      setAllPlayers([]);
      setPlayerPoolCached(false); // Mark as not cached on error
    } finally {
      setLoadingPlayerPool(false);
    }
  };

  const loadTeamPlayers = async (teamShort: string) => {
    try {
      setLoadingPlayers(true);
      setError('');
      console.log('[DEBUG] Loading players for team:', teamShort);
      console.log('[DEBUG] Team short type:', typeof teamShort);
      console.log('[DEBUG] Team short value:', JSON.stringify(teamShort));
      
      if (!teamShort || teamShort.trim() === '') {
        console.error('[DEBUG] Team short name is empty');
        throw new Error('Team short name is required');
      }
      
      // Use league code for API calls
      const leagueCode = getLeagueConfig(selectedLeague).code;
      const requestBody = { team: teamShort.trim() };
      console.log('[DEBUG] API request body:', requestBody, 'league:', leagueCode);
      
      const data = await apiService.getTeamInfoV2(requestBody, leagueCode);
      console.log('[DEBUG] Team data received:', data);
      console.log('[DEBUG] Data type:', typeof data);
      console.log('[DEBUG] Players array:', data?.players);
      console.log('[DEBUG] Players count:', data?.players?.length);
      
      if (!data) {
        console.error('[DEBUG] No data received from API');
        throw new Error('No data received from API');
      }
      
      if (!data.players || !Array.isArray(data.players)) {
        console.warn('[DEBUG] No players array in response:', data);
        console.warn('[DEBUG] Response keys:', Object.keys(data));
        throw new Error(`Invalid response format: players array not found. Response keys: ${Object.keys(data).join(', ')}`);
      }
      
      if (data.players.length === 0) {
        console.warn('[DEBUG] Empty players array received');
        setError('No players found for this team in the database');
        setAllPlayers([]);
        return;
      }
      
      const playersWithIds: LocalTeamPlayerDetail[] = data.players.map((p: any) => {
        // Validate that player_id exists and is valid
        if (!p.player_id || p.player_id === 0) {
          console.warn(`[DEBUG] Player ${p.player_name} has invalid or missing player_id:`, p.player_id);
        }
        return {
          ...p,
          player_id: p.player_id || 0, // Keep as 0 if missing for now, but log warning
          avg_bat_pos: p.avg_bat_pos !== null && p.avg_bat_pos !== undefined && !isNaN(p.avg_bat_pos) ? p.avg_bat_pos : undefined,
          max_tvaa: p.max_tvaa !== undefined && p.max_tvaa !== null ? p.max_tvaa : undefined, // Keep null as undefined (not 0)
          balls_per_innings_bat: p.balls_per_innings_bat !== null && p.balls_per_innings_bat !== undefined ? p.balls_per_innings_bat : undefined,
          balls_per_innings_bowl: p.balls_per_innings_bowl !== null && p.balls_per_innings_bowl !== undefined ? p.balls_per_innings_bowl : undefined,
          wicket_keeper: p.wicket_keeper !== undefined && p.wicket_keeper !== null ? p.wicket_keeper : undefined,
          price: toPrice(p.price),
        };
      });
      
      console.log('[DEBUG] Players loaded successfully:', playersWithIds.length);
      console.log('[DEBUG] First player sample:', playersWithIds[0]);
      
      setAllPlayers(playersWithIds);
      
      // Only clear selected players if team has actually changed (not on first load or when returning to module)
      if (!isFirstLoad && previousTeamId !== null && previousTeamId !== selectedTeamId) {
        console.log('[DEBUG] Team changed, clearing selected players');
    setSelectedPlayers([]);
        setEvaluationResult(null);
    setEvaluationError('');
    } else {
        console.log('[DEBUG] Same team or first load, preserving selected players');
      }
      
      // Mark that first load is complete
      if (isFirstLoad) {
        setIsFirstLoad(false);
      }
      
      setPreviousTeamId(selectedTeamId);
      setError(''); // Clear any previous errors
    } catch (err: any) {
      console.error('[DEBUG] Error loading team players:', err);
      console.error('[DEBUG] Error stack:', err.stack);
      console.error('[DEBUG] Error details:', {
        message: err.message,
        detail: err.detail,
        status: err.status,
        response: err.response
      });
      const errorMessage = err.message || err.detail || err.toString() || 'Unknown error';
      setError(`Failed to load team players: ${errorMessage}`);
      setAllPlayers([]);
    } finally {
      setLoadingPlayers(false);
    }
  };

  const handleTeamSelect = (team: TeamMapping) => {
    console.log('[DEBUG] Team selected:', team);
    // Backend returns team_name as the short code (e.g., "RR", "CSK")
    const teamShort = team.team_name || team.team || '';
    console.log('[DEBUG] Team short name extracted:', teamShort);
    console.log('[DEBUG] Team object keys:', Object.keys(team));
    
    // Only clear selected players if switching to a different team
    if (selectedTeamId !== null && selectedTeamId !== team.team_id) {
      console.log('[DEBUG] Switching to different team, clearing selected players');
    setSelectedPlayers([]);
      setEvaluationResult(null);
      setEvaluationError('');
    }
    
    setSelectedTeamId(team.team_id);
    setSelectedTeamShort(teamShort);
  };

  const handleAddPlayer = (player: LocalTeamPlayerDetail) => {
    const hasImpactSubstitute = currentLeagueConfig.hasImpactSubstitute;
    const maxPlayers = hasImpactSubstitute ? 12 : 11;
    
    if (selectedPlayers.length >= maxPlayers) {
      const errorMsg = hasImpactSubstitute 
        ? 'Maximum 12 players allowed (11 playing XI + 1 substitute)'
        : 'Maximum 11 players allowed (playing XI only)';
      setEvaluationError(errorMsg);
      return;
    }
    
    const nextPosition = selectedPlayers.length < 11 ? selectedPlayers.length + 1 : 12;
    const isAddingAsImpactSubstitute = nextPosition === 12 && hasImpactSubstitute;
    
    // Check overseas count if adding as impact substitute
    const localNationality = currentLeagueConfig.localNationality;
    const maxOverseas = currentLeagueConfig.maxOverseas;
    if (isAddingAsImpactSubstitute && player.nationality !== localNationality) {
      const playingXI = selectedPlayers.slice(0, 11);
      const overseasCountInPlayingXI = playingXI.filter(p => p.nationality !== localNationality).length;
      
      if (overseasCountInPlayingXI >= maxOverseas) {
        setEvaluationError(`Cannot add overseas player as impact substitute. Playing XI already has ${maxOverseas} overseas players (maximum allowed including impact substitute).`);
        return;
      }
    }
    
    const newPlayer: SelectedPlayer = {
      ...player,
      batting_order: nextPosition,
      is_impact: nextPosition === 12 && hasImpactSubstitute ? 1 : 0,
    };
    
    setSelectedPlayers([...selectedPlayers, newPlayer]);
    setEvaluationError('');
  };

  const handleRemovePlayer = (playerName: string) => {
    const removedPlayer = selectedPlayers.find(p => p.player_name === playerName);
    const wasInPlayingXI = removedPlayer && removedPlayer.batting_order <= 11;
    const hasImpactSubstitute = selectedPlayers.length === 12 && selectedPlayers[11].is_impact === 1;
    
    let updatedPlayers = selectedPlayers.filter(p => p.player_name !== playerName);
    
    // If a player was removed from playing XI and we have an impact substitute
    if (wasInPlayingXI && hasImpactSubstitute && autoMoveImpactPlayer) {
      // Auto-move impact player to playing XI (default behavior)
      updatedPlayers = updatedPlayers.map((p, idx) => ({
        ...p,
        batting_order: idx < 11 ? idx + 1 : 12,
        is_impact: idx >= 11 ? 1 : 0,
      }));
    } else if (wasInPlayingXI && hasImpactSubstitute && !autoMoveImpactPlayer) {
      // Don't auto-move: keep impact player in impact position, only reorder playing XI
      const playingXI = updatedPlayers.slice(0, 10); // First 10 remain as playing XI
      const impactSub = updatedPlayers.length > 10 ? updatedPlayers[10] : null;
      
      // Reorder playing XI positions
      const reorderedPlayingXI = playingXI.map((p, idx) => ({
        ...p,
        batting_order: idx + 1,
        is_impact: 0,
      }));
      
      // Keep impact substitute in place
      if (impactSub) {
        updatedPlayers = [...reorderedPlayingXI, { ...impactSub, batting_order: 12, is_impact: 1 }];
      } else {
        updatedPlayers = reorderedPlayingXI;
      }
    } else {
      // Normal reordering (removing impact player or no impact player exists)
      updatedPlayers = updatedPlayers.map((p, idx) => ({
        ...p,
        batting_order: idx < 11 ? idx + 1 : 12,
        is_impact: idx >= 11 ? 1 : 0,
      }));
    }
    
    setSelectedPlayers(updatedPlayers);
  };

  const handleMovePlayerUp = (index: number) => {
    if (index === 0 || index >= 11) return;
    const newPlayers = [...selectedPlayers];
    [newPlayers[index - 1], newPlayers[index]] = [newPlayers[index], newPlayers[index - 1]];
    newPlayers.forEach((p, idx) => {
      p.batting_order = idx < 11 ? idx + 1 : 12;
    });
    setSelectedPlayers(newPlayers);
  };

  const handleMovePlayerDown = (index: number) => {
    if (index >= Math.min(selectedPlayers.length - 1, 10)) return;
    const newPlayers = [...selectedPlayers];
    [newPlayers[index], newPlayers[index + 1]] = [newPlayers[index + 1], newPlayers[index]];
    newPlayers.forEach((p, idx) => {
      p.batting_order = idx < 11 ? idx + 1 : 12;
    });
    setSelectedPlayers(newPlayers);
  };

  const handleChangePosition = (playerIndex: number, newPosition: number) => {
    // Allow positions 1-12
    if (newPosition < 1 || newPosition > 12) return;
    
    const newPlayers = [...selectedPlayers];
    const playingXI = newPlayers.slice(0, 11);
    const substitute = newPlayers.length > 11 ? newPlayers[11] : null;
    const isImpactSubstitute = playerIndex >= 11;
    
    // If moving to position 12 (impact substitute position)
    if (newPosition === 12) {
      if (isImpactSubstitute) {
        // Already impact substitute, just update batting_order
        if (substitute) {
          substitute.batting_order = 12;
          substitute.is_impact = 1;
          setSelectedPlayers([...playingXI, substitute]);
        }
        return;
      } else {
        // Moving playing XI player to impact substitute
        const playerToMove = playingXI[playerIndex];
        
        // Check overseas count if moving overseas player to impact substitute
        const localNat = currentLeagueConfig.localNationality;
        const maxOS = currentLeagueConfig.maxOverseas;
        if (playerToMove.nationality !== localNat) {
          // Count overseas players in remaining playing XI (after removing this player)
          const remainingPlayingXI = playingXI.filter((_, idx) => idx !== playerIndex);
          const overseasCountInRemainingXI = remainingPlayingXI.filter(p => p.nationality !== localNat).length;
          
          if (overseasCountInRemainingXI >= maxOS) {
            setEvaluationError(`Cannot move overseas player to impact substitute. Remaining playing XI already has ${maxOS} overseas players (maximum allowed including impact substitute).`);
            return;
          }
        }
        
        // Remove from playing XI
        playingXI.splice(playerIndex, 1);
        
        // Reassign batting orders for remaining playing XI
        playingXI.forEach((p, idx) => {
          p.batting_order = idx + 1;
          p.is_impact = 0;
        });
        
        // If there's already an impact substitute, move it to playing XI
        if (substitute) {
          playingXI.push({
            ...substitute,
            batting_order: playingXI.length + 1,
            is_impact: 0,
          });
          // Reassign batting orders again
          playingXI.forEach((p, idx) => {
            p.batting_order = idx + 1;
            p.is_impact = 0;
          });
        }
        
        // Set moved player as impact substitute
        const newImpactSubstitute = {
          ...playerToMove,
          batting_order: 12,
          is_impact: 1,
        };
        
        setSelectedPlayers([...playingXI, newImpactSubstitute]);
        setEvaluationError('');
        return;
      }
    }
    
    // Moving to position 1-11 (playing XI positions)
    if (isImpactSubstitute) {
      // Moving impact substitute to playing XI
      if (!substitute) return;
      
      // Remove from impact substitute position
      const playerToMove = { ...substitute };
      
      // Insert into playing XI at new position
      playingXI.splice(newPosition - 1, 0, {
        ...playerToMove,
        batting_order: newPosition,
        is_impact: 0,
      });
      
      // Reassign batting orders for all playing XI
      playingXI.forEach((p, idx) => {
        p.batting_order = idx + 1;
        p.is_impact = 0;
      });
      
      setSelectedPlayers(playingXI);
    } else {
      // Moving playing XI player within playing XI
      const currentPosition = playingXI[playerIndex].batting_order;
      
      // If position is the same, do nothing
      if (currentPosition === newPosition) return;
      
      // Remove the player from current position
      const playerToMove = playingXI.splice(playerIndex, 1)[0];
      
      // Insert at the new position (newPosition - 1 because array is 0-indexed)
      playingXI.splice(newPosition - 1, 0, playerToMove);
      
      // Reassign batting orders based on array order
      playingXI.forEach((p, idx) => {
        p.batting_order = idx + 1;
        p.is_impact = 0;
      });
      
      // Reconstruct the full array
      if (substitute) {
        setSelectedPlayers([...playingXI, substitute]);
      } else {
        setSelectedPlayers(playingXI);
      }
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id);
    
    // Check if dragging from squad list (player_id format: "player_<player_id>")
    if (typeof active.id === 'string' && active.id.startsWith('player_')) {
      const playerId = parseInt(active.id.replace('player_', ''));
      const player = allPlayers.find(p => p.player_id === playerId);
      if (player) {
        setDraggedPlayer(player);
      }
    }
    // Check if dragging from lineup (position format: "position_<index>")
    else if (typeof active.id === 'string' && active.id.startsWith('position_')) {
      const index = parseInt(active.id.replace('position_', ''));
      const player = selectedPlayers[index];
      if (player) {
        setDraggedPlayer({
          player_id: player.player_id,
          player_name: player.player_name,
          nationality: player.nationality,
          role: player.role,
          bat_tvaa_per_over: player.bat_tvaa_per_over ?? null,
          bowl_tvaa_per_over: player.bowl_tvaa_per_over ?? null,
          max_tvaa: player.max_tvaa,  // Keep original value (can be undefined/null)
        });
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setDraggedPlayer(null);
    
    if (!over) return;
    
    const activeId = active.id.toString();
    const overId = over.id.toString();
    
    // If dragging from squad list to lineup position
    if (activeId.startsWith('player_')) {
      const playerId = parseInt(activeId.replace('player_', ''));
      const player = allPlayers.find(p => p.player_id === playerId);
      
      if (!player || selectedPlayerNamesSet.has(player.player_name)) {
        return; // Player already selected or not found
      }
      
      // Check if dropping on playing XI position (1-11) or impact substitute (12)
      if (overId.startsWith('position_')) {
        const targetPosition = parseInt(overId.replace('position_', ''));
        
        if (targetPosition < 11) {
          // Dropping on playing XI position
          if (selectedPlayers.length >= 12) {
            setEvaluationError('Maximum 12 players allowed. Remove a player first.');
            return;
          }
          
          // Insert at position (targetPosition is 0-indexed)
          const newPlayers = [...selectedPlayers];
          const playingXI = newPlayers.slice(0, 11);
          const substitute = newPlayers.length > 11 ? newPlayers[11] : null;
          
          // Remove player if already exists at target position
          if (playingXI[targetPosition]) {
            playingXI.splice(targetPosition, 1);
          }
          
          // Insert new player at target position
          const newPlayer: SelectedPlayer = {
            ...player,
            batting_order: targetPosition + 1,
            is_impact: 0,
          };
          playingXI.splice(targetPosition, 0, newPlayer);
          
          // Reassign batting orders
          playingXI.forEach((p, idx) => {
            p.batting_order = idx + 1;
          });
          
          // Reconstruct array
          if (substitute) {
            setSelectedPlayers([...playingXI, substitute]);
          } else {
            setSelectedPlayers(playingXI);
          }
        } else if (targetPosition === 11) {
          // Dropping on impact substitute position
          if (selectedPlayers.length >= 12) {
            setEvaluationError('Impact substitute already selected. Remove it first.');
            return;
          }
          
          // Check overseas count if dropping overseas player as impact substitute
          const localNat = currentLeagueConfig.localNationality;
          const maxOS = currentLeagueConfig.maxOverseas;
          if (player.nationality !== localNat) {
            const playingXI = selectedPlayers.slice(0, 11);
            const overseasCountInPlayingXI = playingXI.filter(p => p.nationality !== localNat).length;
            
            if (overseasCountInPlayingXI >= maxOS) {
              setEvaluationError(`Cannot add overseas player as impact substitute. Playing XI already has ${maxOS} overseas players (maximum allowed including impact substitute).`);
              return;
            }
          }
          
          const newPlayer: SelectedPlayer = {
            ...player,
            batting_order: 12,
            is_impact: 1,
          };
          
          setSelectedPlayers([...selectedPlayers, newPlayer]);
          setEvaluationError('');
        }
      }
    }
    // If dragging within lineup (reordering)
    else if (activeId.startsWith('position_') && overId.startsWith('position_')) {
      const fromIndex = parseInt(activeId.replace('position_', ''));
      const toIndex = parseInt(overId.replace('position_', ''));
      
      if (fromIndex === toIndex) return;
      
      const newPlayers = [...selectedPlayers];
      const movedPlayer = newPlayers[fromIndex];
      const wasImpactSubstitute = fromIndex === 11 && movedPlayer?.is_impact === 1;
      const isMovingToPlayingXI = toIndex < 11;
      
      // Special case: Dragging impact substitute into playing XI
      if (wasImpactSubstitute && isMovingToPlayingXI) {
        // Get current playing XI BEFORE removing impact substitute
        // This ensures we have the correct 11 players
        const playingXI = newPlayers.slice(0, 11);
        
        // Remove the impact substitute from position 11 (if it exists)
        const hasImpactSub = newPlayers.length > 11;
        if (hasImpactSub) {
          newPlayers.splice(11, 1);
        }
        
        // If there's already a player at the target position, we need to handle it
        if (playingXI[toIndex]) {
          // Remove the player at target position
          const displacedPlayer = playingXI.splice(toIndex, 1)[0];
          
          // Insert impact substitute at target position
          playingXI.splice(toIndex, 0, {
            ...movedPlayer,
            batting_order: toIndex + 1,
            is_impact: 0,
          });
          
          // Reassign batting orders for playing XI
          playingXI.forEach((p, idx) => {
            p.batting_order = idx + 1;
            p.is_impact = 0;
          });
          
          // Handle the displaced player based on auto-move setting
          if (autoMoveImpactPlayer) {
            // Auto-move: displaced player becomes new impact substitute
            const newImpactSubstitute: SelectedPlayer = {
              ...displacedPlayer,
              batting_order: 12,
              is_impact: 1,
            };
            setSelectedPlayers([...playingXI, newImpactSubstitute]);
          } else {
            // Don't auto-move: just remove the displaced player
            // Keep impact substitute slot empty
            setSelectedPlayers(playingXI);
          }
        } else {
          // Target position is empty, just insert
          playingXI.splice(toIndex, 0, {
            ...movedPlayer,
            batting_order: toIndex + 1,
            is_impact: 0,
          });
          
          // Reassign batting orders
          playingXI.forEach((p, idx) => {
            p.batting_order = idx + 1;
            p.is_impact = 0;
          });
          
          setSelectedPlayers(playingXI);
        }
      }
      // Special case: Dragging playing XI player to impact substitute position
      else if (!wasImpactSubstitute && toIndex === 11) {
        // Remove player from playing XI
        const playingXI = newPlayers.slice(0, 11);
        const [movedPlayerFromXI] = playingXI.splice(fromIndex, 1);
        
        // Check overseas count if moving overseas player to impact substitute
        const localNat2 = currentLeagueConfig.localNationality;
        const maxOS2 = currentLeagueConfig.maxOverseas;
        if (movedPlayerFromXI.nationality !== localNat2) {
          // Count overseas players in remaining playing XI (after removing this player)
          const overseasCountInRemainingXI = playingXI.filter(p => p.nationality !== localNat2).length;
          
          if (overseasCountInRemainingXI >= maxOS2) {
            setEvaluationError(`Cannot move overseas player to impact substitute. Remaining playing XI already has ${maxOS2} overseas players (maximum allowed including impact substitute).`);
            return;
          }
        }
        
        // Reassign batting orders for remaining playing XI
        playingXI.forEach((p, idx) => {
          p.batting_order = idx + 1;
          p.is_impact = 0;
        });
        
        // Move player to impact substitute position
        const newImpactSubstitute: SelectedPlayer = {
          ...movedPlayerFromXI,
          batting_order: 12,
          is_impact: 1,
        };
        
        // If there was already an impact substitute, handle it based on auto-move
        const existingImpactSub = newPlayers.length > 11 ? newPlayers[11] : null;
        if (existingImpactSub && autoMoveImpactPlayer) {
          // Auto-move: existing impact substitute moves to playing XI (fills the gap)
          playingXI.push({
            ...existingImpactSub,
            batting_order: playingXI.length + 1,
            is_impact: 0,
          });
          // Reassign batting orders again
          playingXI.forEach((p, idx) => {
            p.batting_order = idx + 1;
            p.is_impact = 0;
          });
        }
        
        setSelectedPlayers([...playingXI, newImpactSubstitute]);
        setEvaluationError('');
      }
      // Normal case: Reordering within playing XI (both from and to are < 11)
      else if (fromIndex < 11 && toIndex < 11) {
        const [movedPlayer] = newPlayers.splice(fromIndex, 1);
        newPlayers.splice(toIndex, 0, movedPlayer);
        
        // Reassign batting orders and impact status
        newPlayers.forEach((p, idx) => {
          p.batting_order = idx < 11 ? idx + 1 : 12;
          p.is_impact = idx >= 11 ? 1 : 0;
        });
        
        setSelectedPlayers(newPlayers);
      }
    }
  };

  // Memoized selected player names as Set for O(1) lookup performance
  const selectedPlayerNamesSet = useMemo(() => {
    return new Set(selectedPlayers.map(p => p.player_name));
  }, [selectedPlayers]);

  // Memoized filtered players for performance optimization
  // Use debouncedSearchQuery instead of searchQuery to reduce filtering operations
  const filteredPlayers = useMemo(() => {
    let filtered = allPlayers;

    // Only filter if search query exists (case-insensitive search)
    // Use debounced search query to avoid filtering on every keystroke
    if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
      const queryLower = debouncedSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(p =>
        p.player_name.toLowerCase().includes(queryLower)
      );
    }

    // Only filter by role if not 'all'
    if (roleFilter !== 'all') {
      filtered = filtered.filter(p => {
        // Case-insensitive role comparison
        const playerRole = (p.role || '').toLowerCase();
        const filterRole = roleFilter.toLowerCase();
        return playerRole === filterRole;
      });
    }

    // Only filter by nationality if not 'all'
    if (nationalityFilter !== 'all') {
      filtered = filtered.filter(p => {
        // Case-insensitive nationality comparison
        const playerNationality = (p.nationality || '').trim();
        return playerNationality === nationalityFilter;
      });
    }

    // Exclude already selected players (using Set for O(1) lookup)
    filtered = filtered.filter(p => !selectedPlayerNamesSet.has(p.player_name));

    // Sort players: those with max_tvaa values first, N/A values last
    filtered = [...filtered].sort((a, b) => {
      const aHasTvaa = a.max_tvaa !== undefined && a.max_tvaa !== null;
      const bHasTvaa = b.max_tvaa !== undefined && b.max_tvaa !== null;
      
      // If one has TVAA and the other doesn't, put the one with TVAA first
      if (aHasTvaa && !bHasTvaa) return -1;
      if (!aHasTvaa && bHasTvaa) return 1;
      
      // If both have TVAA, sort by value (descending)
      if (aHasTvaa && bHasTvaa) {
        return (b.max_tvaa || 0) - (a.max_tvaa || 0);
      }
      
      // If both are N/A, maintain original order
      return 0;
    });

    return filtered;
  }, [allPlayers, debouncedSearchQuery, roleFilter, nationalityFilter, selectedPlayerNamesSet]);
  
  // Limit visible players for performance (virtual scrolling)
  const visiblePlayers = useMemo(() => {
    return filteredPlayers.slice(0, visiblePlayersLimit);
  }, [filteredPlayers, visiblePlayersLimit]);
  
  const hasMorePlayers = filteredPlayers.length > visiblePlayersLimit;

  const isLineupValid = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const hasImpactSubstitute = currentLeagueConfig.hasImpactSubstitute;
    
    if (selectedPlayers.length < 11) {
      errors.push(`At least 11 players required (current: ${selectedPlayers.length})`);
    }
    
    const playingXI = selectedPlayers.slice(0, 11);
    // Count overseas players - include impact substitute only if league supports it
    const maxPlayersToCheck = hasImpactSubstitute ? 12 : 11;
    const allPlayersInLineup = selectedPlayers.slice(0, maxPlayersToCheck);
    const localNationality = currentLeagueConfig.localNationality;
    const maxOverseas = currentLeagueConfig.maxOverseas;
    const overseasCount = allPlayersInLineup.filter(p => p.nationality !== localNationality).length;
    if (overseasCount > maxOverseas) {
      const errorMsg = hasImpactSubstitute 
        ? `Maximum ${maxOverseas} overseas players allowed (including impact substitute) (current: ${overseasCount})`
        : `Maximum ${maxOverseas} overseas players allowed (current: ${overseasCount})`;
      errors.push(errorMsg);
    }
    
    const bowlingCount = playingXI.filter(p =>
      p.role === 'bowler' || p.role === 'all_rounder'
    ).length;
    if (bowlingCount < 5) {
      errors.push(`At least 5 bowling-capable players required (current: ${bowlingCount})`);
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  };

  const handleEvaluateLineup = async () => {
    const validation = isLineupValid();
    if (!validation.valid) {
      setEvaluationError(validation.errors.join('; '));
      return;
    }

    if (!selectedTeamId) {
      setEvaluationError('Please select a team');
      return;
    }

    try {
      setLoadingEvaluation(true);
      setEvaluationError('');
      setEvaluationResult(null);
      // Clear edited deployments when evaluating a new lineup
      setEditedDeployments({});

      // Validate that selectedTeamId is set
      if (!selectedTeamId) {
        throw new Error('No team selected. Please select a team first.');
      }

      // Note: player_id may be missing/null for some players - backend will look it up by name
      // Log players without player_id for debugging, but don't throw error
      const playersWithoutId = selectedPlayers.filter(p => !p.player_id || p.player_id === 0);
      if (playersWithoutId.length > 0) {
        console.warn('[DEBUG] Players without player_id (backend will look up by name):', playersWithoutId.map(p => p.player_name));
      }

      const players = selectedPlayers.map(p => ({
        player_name: p.player_name,
        BattingOrder: p.batting_order,
        // Send player_id if available, otherwise send null/0 and let backend look it up
        PlayerID: p.player_id && p.player_id !== 0 ? p.player_id : null,
        IsImpact: p.is_impact || 0,
      }));

      console.log('[DEBUG] Request payload:', {
        is_bat_first: 1, // Always batting first
        nationality: currentLeagueConfig.localNationality,
        league: selectedLeague,
        players_count: players.length,
        players_sample: players.slice(0, 3), // Log first 3 players
      });

      const request = {
        is_bat_first: 1, // Always batting first
        nationality: currentLeagueConfig.localNationality,
        league: selectedLeague,
        players: players,
      };
      
      console.log('[DEBUG] Final request object:', JSON.stringify(request, null, 2));

      const result = await apiService.evaluateCustomLineupPlayground(request);
      console.log('[DEBUG] Evaluation result received:', result);
      console.log('[DEBUG] Bowl TV value:', result.bowl_tv);
      console.log('[DEBUG] Bat TV value:', result.bat_tv);
      console.log('[DEBUG] Sub details:', result.sub_details);
      
      // Calculate bowl_tv if not present (fallback for old API responses)
      if (result.bowl_tv === undefined || result.bowl_tv === null) {
        const bowl5 = (result as any)['5_bowl_tv'] || 0;
        const bowl6 = (result as any)['6_bowl_tv'] || 0;
        const bowl7 = (result as any)['7_bowl_tv'] || 0;
        result.bowl_tv = 0.19 * bowl5 + 0.63 * bowl6 + 0.18 * bowl7;
        console.log('[DEBUG] Calculated bowl_tv from individual values:', result.bowl_tv);
      }
      
      setEvaluationResult(result);
      setEditedDeployments({});
    } catch (err: any) {
      console.error('Error evaluating lineup:', err);
      setEvaluationError(err.message || 'Failed to evaluate lineup');
    } finally {
      setLoadingEvaluation(false);
    }
  };



  // ---------------------------------------------------------------------------
  // Auto-pilot registration: while this page is mounted, the assistant's
  // Auto-pilot mode can read its state and drive it (build/evaluate XIs).
  // Handlers close over fresh state — registration refreshes every render.
  // ---------------------------------------------------------------------------
  useAutopilotPage({
    page: 'playground',
    describeState: () => ({
      league: selectedLeague,
      league_rules: {
        code: currentLeagueConfig.code,
        max_overseas: currentLeagueConfig.maxOverseas,
        local_nationality: currentLeagueConfig.localNationality,
        impact_substitute_allowed: currentLeagueConfig.hasImpactSubstitute,
      },
      team: selectedTeamShort || null,
      team_id: selectedTeamId,
      player_source: playerSource,
      pool_count: allPlayers.length,
      current_xi: selectedPlayers.map((p) => ({
        name: p.player_name,
        order: p.batting_order,
        is_impact: p.is_impact,
      })),
      last_evaluation: evaluationResult
        ? { bat_tv: evaluationResult.bat_tv, bowl_tv: evaluationResult.bowl_tv }
        : null,
    }),
    actions: {
      set_league: ({ league }: { league: string }) => {
        handleLeagueChange(String(league || '').toUpperCase());
      },
      select_team: ({ team }: { team: string | number }) => {
        const t = teams.find(
          (x: any) =>
            x.team_id === Number(team) ||
            String(x.team_name || '').toLowerCase() === String(team).toLowerCase()
        );
        if (t) handleTeamSelect(t as any);
      },
      set_player_source: ({ source }: { source: string }) => {
        setPlayerSource(source === 'pool' ? 'pool' : 'team');
      },
      // The agent evaluated server-side; build the XI on the page exactly as if
      // the user had picked the players by hand.
      apply_lineup: (args: { players?: any[]; label?: string }) => {
        const players = (args.players || []).map((p: any, i: number) => ({
          ...p,
          player_name: p.player_name || p.name,
          batting_order: p.batting_order ?? i + 1,
          is_impact: p.is_impact ?? 0,
        }));
        setSelectedPlayers(players as any);
        setEvaluationError('');
      },
      apply_evaluation: (args: { result?: any }) => {
        const result = args.result;
        if (!result) return;
        if (result.bowl_tv === undefined || result.bowl_tv === null) {
          const bowl5 = result['5_bowl_tv'] || 0;
          const bowl6 = result['6_bowl_tv'] || 0;
          const bowl7 = result['7_bowl_tv'] || 0;
          result.bowl_tv = 0.19 * bowl5 + 0.63 * bowl6 + 0.18 * bowl7;
        }
        setEvaluationResult(result);
        setEditedDeployments({});
        setEvaluationError('');
      },
      clear_lineup: () => {
        setSelectedPlayers([]);
        setEvaluationResult(null);
        setEvaluationError('');
      },
    },
  });


  const validation = isLineupValid();
  const playingXI = selectedPlayers.slice(0, 11);
  const substitute = selectedPlayers.length === 12 ? selectedPlayers[11] : null;
  const lineupSalary = sumPrices(selectedPlayers);
  const selectedTeam = teams.find(t => t.team_id === selectedTeamId);
  const teamColor = selectedTeam?.team_color || '#1976d2';

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header - Consistent with Trade Machine */}
      <Box sx={{ mb: 4, mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SportsCricket color="primary" fontSize="large" />
            Lineup Playground
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Build your custom lineup by selecting players and setting their batting order
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* League Selector Dropdown */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="league-select-label">League</InputLabel>
            <Select
              labelId="league-select-label"
              id="league-select"
              value={selectedLeague}
              label="League"
              onChange={(e) => handleLeagueChange(e.target.value)}
            >
              {Object.entries(LEAGUE_CONFIG).map(([code, config]) => (
                <MenuItem key={code} value={code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{config.name}</Typography>
                    <Chip 
                      label={config.code} 
                      size="small" 
                      sx={{ fontSize: '0.65rem', height: 18 }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh data from BigQuery without restarting the app">
            <Button
              variant="outlined"
              color="primary"
              startIcon={reloadingData ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
              onClick={handleReloadData}
              disabled={reloadingData}
              sx={{ 
                minWidth: 140,
                whiteSpace: 'nowrap',
              }}
            >
              {reloadingData ? 'Refreshing...' : 'Refresh Data'}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Team Selection Grid */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Select Team
        </Typography>
        {loadingTeams ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: 'repeat(2, 1fr)', 
              sm: selectedLeague === 'THE_HUNDRED' ? 'repeat(4, 1fr)' : (teams.length <= 6 ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)'), 
              md: selectedLeague === 'THE_HUNDRED' ? 'repeat(4, 1fr)' : (teams.length <= 6 ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)') 
            }, 
            gap: 2,
            maxWidth: selectedLeague === 'THE_HUNDRED' ? '1000px' : (teams.length <= 6 ? '900px' : '100%'),
            mx: selectedLeague === 'THE_HUNDRED' ? 'auto' : (teams.length <= 6 ? 'auto' : 0),
          }}>
            {teams.map((team) => {
              const isSelected = selectedTeamId === team.team_id;
              // Backend returns team_name as the short code (e.g., "RR", "CSK")
              const teamShort = team?.team_name || team?.team || '';
              const logoPath = getTeamLogo(teamShort, selectedLeague);
              
              return (
                <Box key={team.team_id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: isSelected ? 2 : 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'rgba(25, 118, 210, 0.03)' : 'background.paper',
                      transition: 'all 0.2s',
                      position: 'relative',
                      boxShadow: isSelected ? 2 : 1,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                      },
                    }}
                    onClick={() => handleTeamSelect(team)}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2, position: 'relative' }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 120,
                          margin: '0 auto',
                          mb: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isSelected ? 'rgba(25, 118, 210, 0.02)' : 'background.paper',
                          border: isSelected ? '1px solid' : '1px solid',
                          borderColor: isSelected ? 'rgba(25, 118, 210, 0.3)' : 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                          position: 'relative',
                          p: 1,
                        }}
                      >
                        {logoPath ? (
                          <img 
                            src={logoPath} 
                            alt={teamShort || team.team_name || 'Team'}
                            style={{ 
                              maxWidth: '85%', 
                              maxHeight: '85%', 
                              width: 'auto',
                              height: 'auto',
                              objectFit: 'contain',
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const fallback = target.nextElementSibling as HTMLElement;
                              if (fallback) fallback.style.display = 'block';
                            }}
                          />
                        ) : null}
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: 'text.primary',
                            display: logoPath ? 'none' : 'block'
                          }}
                        >
                          {teamShort || team.team_name || 'N/A'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {team.team_full_name || team.team_name || 'Unknown Team'}
                      </Typography>
            </CardContent>
          </Card>
                </Box>
              );
            })}
          </Box>
        )}
        </Box>

      {/* Wrap entire lineup section with DndContext for drag-and-drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
      {(selectedTeamId || playerSource === 'pool') && (
        <>
          {/* Player Source Toggle (Team Squad vs Player Pool) */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={playerSource}
              exclusive
              onChange={(e, value) => value !== null && setPlayerSource(value)}
              size="medium"
            >
              <ToggleButton value="team">
                <Typography sx={{ fontWeight: 'bold' }}>Team Squad</Typography>
              </ToggleButton>
              <ToggleButton value="pool">
                <Typography sx={{ fontWeight: 'bold' }}>Player Pool</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>


          {(loadingPlayers || loadingPlayerPool) ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {playerSource === 'pool' ? 'Loading player pool...' : 'Loading squad...'}
              </Typography>
          </Box>
          ) : allPlayers.length === 0 && !loadingPlayers && !loadingPlayerPool ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {playerSource === 'pool' ? 'No player pool data available.' : 'No squad data available for this team.'}
              </Typography>
              {error && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              {selectedTeamShort && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Team: {selectedTeamShort}
                </Typography>
              )}
            </Box>
          ) : allPlayers.length > 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              {/* Left Column: Available Players */}
              <Box>
                <Paper elevation={0} sx={{ border: '2px solid', borderColor: playerSource === 'pool' ? 'primary.main' : teamColor, borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ p: 2, bgcolor: playerSource === 'pool' ? 'primary.main' : teamColor, color: 'white' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SportsCricket />
                          {playerSource === 'pool' 
                            ? 'Player Pool' 
                            : (selectedTeam?.team_full_name || selectedTeam?.team_name || 'Team')}
                    </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                          {filteredPlayers.length} of {allPlayers.length} players
                          {roleFilter !== 'all' && ` • ${getRoleLabel(roleFilter)}`}
                          {nationalityFilter !== 'all' && ` • ${nationalityFilter}`}
                        </Typography>
                  </Box>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    {/* Search Box */}
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search players..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchQuery('')}>
                              <Clear />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Filters */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      {/* Role Filter */}
                      <FormControl size="small" fullWidth>
                        <InputLabel>Filter by Role</InputLabel>
                        <Select
                          value={roleFilter}
                          label="Filter by Role"
                          onChange={(e) => setRoleFilter(e.target.value)}
                        >
                          <MenuItem value="all">All Roles</MenuItem>
                          <MenuItem value="top_order">Top Order</MenuItem>
                          <MenuItem value="all_rounder">All Rounder</MenuItem>
                          <MenuItem value="middle_lower_order">Middle/Lower</MenuItem>
                          <MenuItem value="bowler">Bowler</MenuItem>
                        </Select>
                      </FormControl>

                      {/* Nationality Filter */}
                      <FormControl size="small" fullWidth>
                        <InputLabel>Filter by Nationality</InputLabel>
                        <Select
                          value={nationalityFilter}
                          label="Filter by Nationality"
                          onChange={(e) => setNationalityFilter(e.target.value)}
                        >
                          <MenuItem value="all">All Nationalities</MenuItem>
                          {uniqueNationalities.map((nat) => (
                            <MenuItem key={nat} value={nat}>
                              {nat}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <SortableContext
                  items={visiblePlayers.map(p => `player_${p.player_id}`)}
                  strategy={verticalListSortingStrategy}
                >
                <List sx={{ maxHeight: 600, overflow: 'auto', listStyleType: 'none', padding: 0 }}>
                  {visiblePlayers.map((player, index) => {
                    const isSelected = selectedPlayers.some(p => p.player_name === player.player_name);
                    const borderColor = playerSource === 'pool' ? 'primary.main' : teamColor;
                    const hasNoTvaa = player.max_tvaa === undefined || player.max_tvaa === null;
                    return (
                      <DraggablePlayerItem
                        key={player.player_id || index}
                        player={player}
                        isSelected={isSelected}
                        borderColor={borderColor}
                        onAddClick={() => handleAddPlayer(player)}
                        disabled={selectedPlayers.length >= 12 || hasNoTvaa}
                      />
                    );
                  })}
                  {hasMorePlayers && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <Button
                                    variant="outlined"
                        onClick={() => setVisiblePlayersLimit((prev: number) => prev + 50)}
                        sx={{ minWidth: 150 }}
                      >
                        Load More ({filteredPlayers.length - visiblePlayersLimit} remaining)
                      </Button>
                                </Box>
                  )}
                    </List>
                </SortableContext>
              </Box>
                  </Paper>
            </Box>

              {/* Right Column: Selected Lineup */}
            <Box>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Your Lineup ({selectedPlayers.length}/12)
                    </Typography>
                    {validation.valid ? (
                      <Chip
                          icon={<Check />}
                        label="Valid"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="Invalid"
                        color="error"
                        size="small"
                      />
                    )}
                  </Box>

                  {!validation.valid && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {validation.errors.map((err, idx) => (
                        <Typography key={idx} variant="body2">{err}</Typography>
                      ))}
                    </Alert>
                  )}

                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                      <Chip
                        label={`Playing XI: ${playingXI.length}/11`}
                        size="small"
                        color={playingXI.length === 11 ? 'success' : 'default'}
                      />
                      {currentLeagueConfig.hasImpactSubstitute && (
                        <Chip
                          label={`Substitute: ${substitute ? '1' : '0'}/1`}
                          size="small"
                          color={substitute ? 'success' : 'default'}
                        />
                      )}
                      <Chip
                        label={`Overseas: ${playingXI.filter(p => p.nationality !== currentLeagueConfig.localNationality).length}/${currentLeagueConfig.maxOverseas}`}
                        size="small"
                        color={playingXI.filter(p => p.nationality !== currentLeagueConfig.localNationality).length <= currentLeagueConfig.maxOverseas ? 'success' : 'error'}
                      />
                      <Chip
                        label={`Bowlers: ${playingXI.filter(p => p.role === 'bowler' || p.role === 'all_rounder').length}`}
                        size="small"
                        color={playingXI.filter(p => p.role === 'bowler' || p.role === 'all_rounder').length >= 5 ? 'success' : 'warning'}
                      />
                      <Chip
                        label={`WK: ${playingXI.filter(p => p.wicket_keeper === 1).length}`}
                        size="small"
                        color={playingXI.filter(p => p.wicket_keeper === 1).length >= 1 ? 'success' : 'warning'}
                      />
                      {currentLeagueConfig.showSalary && (
                        <Tooltip
                          title={
                            lineupSalary.unpriced > 0
                              ? `Combined contract price of selected players; ${lineupSalary.unpriced} without a known price`
                              : 'Combined contract price of all selected players'
                          }
                        >
                          <Chip
                            label={`Salary: ${formatCrores(lineupSalary.total)}${lineupSalary.unpriced > 0 ? ` (+${lineupSalary.unpriced} n/a)` : ''}`}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </Stack>

                    {/* Auto-move toggle - only show for leagues with impact substitute */}
                    {currentLeagueConfig.hasImpactSubstitute && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Tooltip title="When enabled, removing a player from playing XI will automatically move the impact substitute to fill the gap. When disabled, the impact substitute stays in place.">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={autoMoveImpactPlayer}
                              onChange={(e) => setAutoMoveImpactPlayer(e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                              Auto-move impact substitute
                            </Typography>
                          }
                        />
                      </Tooltip>
                  </Box>
                    )}

                    {selectedPlayers.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                          No players selected. Add players from the left panel to build your lineup.
                      </Typography>
                    </Paper>
                  ) : (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          Playing XI (Batting Order)
                                  </Typography>
                        <Paper variant="outlined" sx={{ mb: 2, ...(playingXI.length === 11 ? {} : { maxHeight: 400, overflow: 'auto' }) }}>
                          <SortableContext
                            items={selectedPlayers.map((_, idx) => `position_${idx}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            <List dense sx={{ listStyleType: 'none', padding: 0 }}>
                              {playingXI.map((player, index) => (
                                <DroppableLineupPosition
                                  key={player.player_id}
                                  player={player}
                                  position={player.batting_order}
                                  index={index}
                                  isImpactSubstitute={false}
                                  onRemove={() => handleRemovePlayer(player.player_name)}
                                  onChangePosition={(newPosition) => handleChangePosition(index, newPosition)}
                                  onMoveUp={() => handleMovePlayerUp(index)}
                                  onMoveDown={() => handleMovePlayerDown(index)}
                                  evaluationResult={evaluationResult}
                                  localNationality={currentLeagueConfig.localNationality}
                                />
                              ))}
                              {/* Empty slots for remaining positions */}
                              {Array.from({ length: Math.max(0, 11 - playingXI.length) }, (_, i) => (
                                <DroppableLineupPosition
                                  key={`empty_${playingXI.length + i}`}
                                  player={null}
                                  position={playingXI.length + i + 1}
                                  index={playingXI.length + i}
                                  isImpactSubstitute={false}
                                  onRemove={() => {}}
                                  onChangePosition={() => {}}
                                  evaluationResult={null}
                                  localNationality={currentLeagueConfig.localNationality}
                                />
                              ))}
                      </List>
                          </SortableContext>
                    </Paper>

                        {/* Impact Substitute section - only show for leagues with impact substitute */}
                        {currentLeagueConfig.hasImpactSubstitute && (
                          substitute ? (
                            <>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Impact Substitute
                              </Typography>
                              <Paper variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
                                <DroppableLineupPosition
                                  player={substitute}
                                  position={12}
                                  index={11}
                                  isImpactSubstitute={true}
                                  onRemove={() => handleRemovePlayer(substitute.player_name)}
                                  onChangePosition={(newPosition) => handleChangePosition(11, newPosition)}
                                  evaluationResult={evaluationResult}
                                  localNationality={currentLeagueConfig.localNationality}
                                />
                              </Paper>
                            </>
                          ) : (
                            <>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Impact Substitute
                              </Typography>
                              <Paper variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
                                <DroppableLineupPosition
                                  player={null}
                                  position={12}
                                  index={11}
                                  isImpactSubstitute={true}
                                  onRemove={() => {}}
                                  onChangePosition={() => {}}
                                  evaluationResult={null}
                                  localNationality={currentLeagueConfig.localNationality}
                                />
                              </Paper>
                            </>
                          )
                        )}
                      </>
                  )}

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={loadingEvaluation ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <PlayArrow />}
                      onClick={handleEvaluateLineup}
                      disabled={!validation.valid || loadingEvaluation}
                      sx={{ mt: 2 }}
                    >
                      {loadingEvaluation ? 'Evaluating...' : 'Evaluate Lineup'}
                    </Button>

                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      startIcon={<TrendingUp />}
                      onClick={() => {
                        navigate('/batting-order-optimizer', {
                          state: {
                            players: playingXI.map((p) => ({
                              player_id: p.player_id,
                              player_name: p.player_name,
                              batting_order: p.batting_order,
                              role: p.role,
                              nationality: p.nationality,
                            })),
                            squadPlayers: allPlayers.map((p) => {
                              const sel = selectedPlayers.find(
                                (s) => s.player_id === p.player_id,
                              );
                              return {
                                player_id: p.player_id,
                                player_name: p.player_name,
                                batting_order:
                                  sel?.batting_order ??
                                  (substitute?.player_id === p.player_id ? 12 : 999),
                                role: p.role,
                                nationality: p.nationality,
                              };
                            }),
                            nationality: currentLeagueConfig.localNationality,
                            league: selectedLeague,
                          },
                        });
                      }}
                      disabled={playingXI.length < 11}
                      sx={{ mt: 1 }}
                    >
                      Optimize Batting Order
                    </Button>

                  {evaluationError && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setEvaluationError('')}>
                      {evaluationError}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Box>
            </Box>
        ) : null}

          {/* Evaluation Results */}
          {evaluationResult && (
            <Box sx={{ mt: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Evaluation Results
              </Typography>

                  {/* Main TV Metrics - Centered */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'action.hover', minWidth: 150 }}>
                      <Typography variant="caption" color="text.secondary">Batting TV</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {evaluationResult.bat_tv?.toFixed(2) || 'N/A'}
                      </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'action.hover', minWidth: 150 }}>
                      <Typography variant="caption" color="text.secondary">Bowling TV</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {calculatedBowlingTV !== null && calculatedBowlingTV !== undefined
                          ? calculatedBowlingTV.toFixed(2)
                          : evaluationResult.bowl_tv !== undefined && evaluationResult.bowl_tv !== null 
                            ? evaluationResult.bowl_tv.toFixed(2) 
                            : 'N/A'}
                      </Typography>
                    </Paper>
                  </Box>


                  {/* Bowling Deployment - 5 Bowlers */}
                  {evaluationResult['5_bowl_deployment'] && evaluationResult['5_bowl_deployment'].length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <EditableBowlingDeployment
                        deployment={evaluationResult['5_bowl_deployment']}
                        originalTV={evaluationResult['5_bowl_tv'] || 0}
                        deploymentType="5"
                        tvaaPhaseProj={buildDeploymentTvaaProj(evaluationResult['5_bowl_deployment'])}
                        xiBowlerPhaseProj={evaluationResult.xi_bowler_phase_proj}
                        updatedDeployment={editedDeployments['5']?.deployment}
                        updatedTV={editedDeployments['5']?.tv}
                        onUpdate={(updatedDeployment, updatedTV) => {
                          setEditedDeployments(prev => ({
                            ...prev,
                            '5': { deployment: updatedDeployment, tv: updatedTV }
                          }));
                        }}
                      />
                    </Box>
                  )}

                  {/* Bowling Deployment - 6 Bowlers */}
                  {evaluationResult['6_bowl_deployment'] && evaluationResult['6_bowl_deployment'].length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <EditableBowlingDeployment
                        deployment={evaluationResult['6_bowl_deployment']}
                        originalTV={evaluationResult['6_bowl_tv'] || 0}
                        deploymentType="6"
                        tvaaPhaseProj={buildDeploymentTvaaProj(evaluationResult['6_bowl_deployment'])}
                        xiBowlerPhaseProj={evaluationResult.xi_bowler_phase_proj}
                        updatedDeployment={editedDeployments['6']?.deployment}
                        updatedTV={editedDeployments['6']?.tv}
                        onUpdate={(updatedDeployment, updatedTV) => {
                          setEditedDeployments(prev => ({
                            ...prev,
                            '6': { deployment: updatedDeployment, tv: updatedTV }
                          }));
                        }}
                      />
                    </Box>
                  )}

                  {/* Bowling Deployment - 7 Bowlers */}
                  {evaluationResult['7_bowl_deployment'] && evaluationResult['7_bowl_deployment'].length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <EditableBowlingDeployment
                        deployment={evaluationResult['7_bowl_deployment']}
                        originalTV={evaluationResult['7_bowl_tv'] || 0}
                        deploymentType="7"
                        tvaaPhaseProj={buildDeploymentTvaaProj(evaluationResult['7_bowl_deployment'])}
                        xiBowlerPhaseProj={evaluationResult.xi_bowler_phase_proj}
                        updatedDeployment={editedDeployments['7']?.deployment}
                        updatedTV={editedDeployments['7']?.tv}
                        onUpdate={(updatedDeployment, updatedTV) => {
                          setEditedDeployments(prev => ({
                            ...prev,
                            '7': { deployment: updatedDeployment, tv: updatedTV }
                          }));
                        }}
                      />
                    </Box>
                  )}
            </CardContent>
          </Card>
      </Box>
          )}
        </>
      )}
      {/* Drag overlay for visual feedback */}
      <DragOverlay>
        {draggedPlayer ? (
          <Paper
            elevation={8}
            sx={{
              p: 2,
              minWidth: 200,
              bgcolor: 'background.paper',
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
            }}
          >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DragIndicator sx={{ color: 'primary.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {draggedPlayer.player_name}
              </Typography>
                                  <Chip
                label={getRoleLabel(draggedPlayer.role)}
                                    size="small"
                color={getRoleColor(draggedPlayer.role) as any}
                sx={{ fontSize: '0.7rem' }}
              />
              {draggedPlayer.wicket_keeper === 1 && (
                <Chip
                  label="WK"
                  size="small"
                  color="warning"
                  sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                />
              )}
      </Box>
          </Paper>
        ) : null}
      </DragOverlay>
      </DndContext>
    </Container>
  );
};

// Helper component for draggable player from squad list
const DraggablePlayerItem: React.FC<{
  player: LocalTeamPlayerDetail;
  isSelected: boolean;
  borderColor: string;
  onAddClick: () => void;
  disabled: boolean;
}> = ({ player, isSelected, borderColor, onAddClick, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: `player_${player.player_id}`,
    disabled: isSelected || disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      disableGutters
      onClick={() => !isSelected && !disabled && onAddClick()}
      sx={{
        border: '2px solid',
        borderColor: isSelected ? borderColor : disabled ? 'grey.300' : 'divider',
        borderRadius: 1,
        mb: 1,
        pl: 2,
        pr: 2,
        bgcolor: isSelected ? `${borderColor}10` : disabled ? 'grey.50' : 'transparent',
        cursor: isSelected || disabled ? 'not-allowed' : 'grab',
        opacity: disabled ? 0.6 : 1,
        '&:active': {
          cursor: isSelected || disabled ? 'not-allowed' : 'grabbing',
        },
        '&:hover': { 
          bgcolor: isSelected ? `${borderColor}10` : disabled ? 'grey.50' : 'action.hover' 
        },
        '&::marker': {
          display: 'none',
        },
      }}
    >
      <DragIndicator sx={{ mr: 1, color: borderColor, opacity: isSelected ? 0.3 : 1 }} />
      <PersonOutline sx={{ mr: 2, color: borderColor }} />
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {player.player_name}
                                  </Typography>
                                  <Chip
              label={getRoleLabel(player.role)}
                                    size="small"
                                    color={getRoleColor(player.role) as any}
              sx={{ fontSize: '0.75rem' }}
                                  />
            {player.wicket_keeper === 1 && (
              <Chip
                label="WK"
                size="small"
                color="warning"
                sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
              />
            )}
            <Chip label={player.nationality} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                </Box>
                              }
                              secondary={
          <>
            {player.price !== undefined && (
              <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                Price: <strong>{formatCrores(player.price)}</strong>
              </Typography>
            )}
            {player.avg_bat_pos !== undefined && player.avg_bat_pos !== null && (
              <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                Avg Bat Pos: <strong>{Math.round(player.avg_bat_pos)}</strong>
              </Typography>
            )}
            <Typography variant="caption" component="span" sx={{ mr: 2 }}>
              Max TVAA: <strong>{player.max_tvaa !== undefined && player.max_tvaa !== null ? player.max_tvaa.toFixed(3) : 'N/A'}</strong>
            </Typography>
                                  {player.bat_tvaa_per_over !== null && (
              <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                Bat: {player.bat_tvaa_per_over.toFixed(3)}
                                    </Typography>
                                  )}
                                  {player.bowl_tvaa_per_over !== null && (
              <Typography variant="caption" component="span">
                Bowl: {player.bowl_tvaa_per_over.toFixed(3)}
                                    </Typography>
                                  )}
          </>
                              }
                            />
      {!isSelected && (
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                size="small"
            onClick={(e) => {
              e.stopPropagation();
              onAddClick();
            }}
            disabled={disabled}
            sx={{ color: borderColor }}
          >
            <Add />
                              </IconButton>
                            </ListItemSecondaryAction>
      )}
    </ListItem>
  );
};

// Helper component for droppable lineup position
const DroppableLineupPosition: React.FC<{
  player: SelectedPlayer | null;
  position: number;
  index: number;
  isImpactSubstitute?: boolean;
  onRemove: () => void;
  onChangePosition: (newPosition: number) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  evaluationResult: LineupEvaluation | null;
  localNationality?: string;
}> = ({
  player,
  position,
  index,
  isImpactSubstitute = false,
  onRemove,
  onChangePosition,
  onMoveUp,
  onMoveDown,
  evaluationResult,
  localNationality = 'India',
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [popoverTop, setPopoverTop] = React.useState<number>(0);
  const open = Boolean(anchorEl);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (player) {
      const target = event.currentTarget;
      setAnchorEl(target);
      
      // Calculate vertical position based on player item position
      const rect = target.getBoundingClientRect();
      setPopoverTop(rect.top + rect.height / 2);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // Get player metrics - only player-specific data
  const getPlayerMetrics = () => {
    if (!player) return null;

    // Only player-specific metrics from player data
    return {
      avg_bat_pos: player.avg_bat_pos,
      bat_tvaa_per_over: player.bat_tvaa_per_over,
      bowl_tvaa_per_over: player.bowl_tvaa_per_over,
      balls_per_innings_bat: player.balls_per_innings_bat,
      balls_per_innings_bowl: player.balls_per_innings_bowl,
      price: player.price,
    };
  };

  const playerMetrics = getPlayerMetrics();
  const { attributes, listeners, setNodeRef, transform, isDragging, isOver } = useSortable({
    id: `position_${index}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    border: isOver ? '2px dashed' : 'none',
    borderColor: isOver ? 'primary.main' : 'transparent',
  };

  // Separate drag handle - only the drag indicator should trigger drag
  const dragHandleProps = {
    ...attributes,
    ...listeners,
  };

  // Remove drag listeners from the entire list item
  const itemProps = {
    // Don't spread listeners here - only on drag handle
  };

  if (!player) {
    return (
      <Box
        ref={setNodeRef}
        style={style}
        sx={{
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          mb: 1,
          textAlign: 'center',
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {isImpactSubstitute ? 'Drop impact substitute here' : `Drop player at position ${position}`}
        </Typography>
                  </Box>
    );
  }

  return (
    <>
    <ListItem
      ref={setNodeRef}
      style={style}
      {...itemProps}
      disableGutters
      sx={{
        borderBottom: !isImpactSubstitute ? '1px solid' : 'none',
        borderColor: 'divider',
        bgcolor: index % 2 === 0 ? 'action.hover' : 'transparent',
        pl: 2,
        pr: 2,
        cursor: player ? 'pointer' : 'default',
        '&::marker': {
          display: 'none',
        },
      }}
    >
      <Box
        {...dragHandleProps}
        sx={{
          cursor: 'grab',
          display: 'inline-flex',
          alignItems: 'center',
          mr: 1,
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <DragIndicator sx={{ color: 'primary.main' }} />
      </Box>
      <FormControl size="small" sx={{ minWidth: 60, mr: 1 }}>
        <Select
          value={player.batting_order}
          onChange={(e) => onChangePosition(Number(e.target.value))}
          onClick={(e) => e.stopPropagation()}
          sx={{
            fontWeight: 'bold',
            '& .MuiSelect-select': {
              py: 0.5,
              px: 1,
              textAlign: 'center',
            },
          }}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((pos) => (
            <MenuItem key={pos} value={pos}>
              {pos}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 'bold',
                cursor: player ? 'pointer' : 'default',
              }}
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
            >
              {player.player_name}
            </Typography>
            <Chip
              label={getRoleLabel(player.role)}
              size="small"
              color={getRoleColor(player.role) as any}
              sx={{ height: 18, fontSize: '0.6rem' }}
            />
            {player.wicket_keeper === 1 && (
              <Chip
                label="WK"
                size="small"
                color="warning"
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
              />
            )}
            {player.nationality !== localNationality && (
              <Tooltip title="Overseas">
                <FlightTakeoff sx={{ fontSize: 16, color: 'primary.main' }} aria-label="Overseas" />
              </Tooltip>
            )}
            {player.price !== undefined && (
              <Chip
                label={formatCrores(player.price)}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.6rem' }}
              />
            )}
            </Box>
        }
      />
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {!isImpactSubstitute && onMoveUp && index > 0 && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMoveUp();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            sx={{ zIndex: 10 }}
          >
            <ArrowUpward fontSize="small" />
          </IconButton>
        )}
        {!isImpactSubstitute && onMoveDown && index < 10 && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMoveDown();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            sx={{ zIndex: 10 }}
          >
            <ArrowDownward fontSize="small" />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          color="error"
          sx={{ zIndex: 10 }}
        >
          <Delete fontSize="small" />
        </IconButton>
            </Box>
    </ListItem>
    {/* Player Details Popover on Hover */}
    {player && playerMetrics && (
      <Popover
        id={`player-popover-${player.player_id}`}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        disableRestoreFocus
        sx={{
          pointerEvents: 'none',
        }}
        PaperProps={{
          onMouseEnter: () => setAnchorEl(anchorEl),
          onMouseLeave: handlePopoverClose,
          sx: {
            pointerEvents: 'auto',
            p: 1.5,
            minWidth: 200,
            maxWidth: 250,
            position: 'fixed',
            right: 16, // Fixed at extreme right of viewport
            top: `${popoverTop}px`,
            transform: 'translateY(-50%)', // Center vertically
            ml: 0, // No margin needed with fixed positioning
            zIndex: 1300, // Ensure it's above other elements
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
          {player.player_name}
              </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Player-specific metrics */}
          {playerMetrics.price !== undefined && playerMetrics.price !== null && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Price:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {formatCrores(playerMetrics.price)}
              </Typography>
            </Box>
          )}
          {playerMetrics.avg_bat_pos !== undefined && playerMetrics.avg_bat_pos !== null && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Avg Bat Pos:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {Math.round(playerMetrics.avg_bat_pos)}
              </Typography>
            </Box>
          )}
          {playerMetrics.bat_tvaa_per_over !== null && playerMetrics.bat_tvaa_per_over !== undefined && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Bat TVAA/Over:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {playerMetrics.bat_tvaa_per_over.toFixed(3)}
              </Typography>
      </Box>
          )}
          {playerMetrics.bowl_tvaa_per_over !== null && playerMetrics.bowl_tvaa_per_over !== undefined && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Bowl TVAA/Over:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {playerMetrics.bowl_tvaa_per_over.toFixed(3)}
              </Typography>
            </Box>
          )}
          {playerMetrics.balls_per_innings_bat !== null && playerMetrics.balls_per_innings_bat !== undefined && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Balls/Inn (Bat):</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {Math.round(playerMetrics.balls_per_innings_bat)}
              </Typography>
            </Box>
          )}
          {playerMetrics.balls_per_innings_bowl !== null && playerMetrics.balls_per_innings_bowl !== undefined && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Balls/Inn (Bowl):</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {Math.round(playerMetrics.balls_per_innings_bowl)}
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>
    )}
    </>
  );
};

export default LineupPlayground;